import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { IoChevronDown } from "react-icons/io5";
import { FaGraduationCap, FaClock, FaDollarSign, FaBookOpen, FaAward, FaCheck, FaArrowLeft, FaMapMarkerAlt } from "react-icons/fa";
import { BiCategory } from "react-icons/bi";
import { fetchCourses } from "../../redux/slices/frontend/courseSlice";
import { fetchUserEnrolledCohorts } from "../../redux/slices/userEnrolledCohortSlice";
import { useAuth } from "../../contexts/AuthContext";
import "./PrivateCourseDetails.css";

const PrivateCourseDetails = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useAuth();
  
  const { courses, loading, error } = useSelector((state) => state.courses);
  const { enrollment: userEnrollments } = useSelector((state) => state.userEnrolledCohorts || {});

  const [expandedSections, setExpandedSections] = useState({});
  const [currency, setCurrency] = useState("USD");
  const [usdToNgn, setUsdToNgn] = useState(null);
  const [rateLoading, setRateLoading] = useState(true);

  useEffect(() => {
    dispatch(fetchCourses());
    if (localStorage.getItem("authToken")) {
      dispatch(fetchUserEnrolledCohorts());
    }
  }, [dispatch]);

  // Find target private course by slug
  const course = useMemo(() => {
    if (!courses) return null;
    return courses.find((c) => c.slug === slug && c.type === "private");
  }, [courses, slug]);

  useEffect(() => {
    if (course?.curriculum) {
      setExpandedSections(
        Object.fromEntries(
          (course.curriculum || []).map((item) => [item.title, false])
        )
      );
    }
  }, [course]);

  useEffect(() => {
    const fetchRate = async () => {
      setRateLoading(true);
      try {
        const res = await fetch("https://v6.exchangerate-api.com/v6/689d396c0a109d335cc4092c/latest/USD");
        const data = await res.json();
        setUsdToNgn(data.conversion_rates?.NGN);
      } catch (e) {
        setUsdToNgn(null);
      } finally {
        setRateLoading(false);
      }
    };
    fetchRate();
  }, []);

  const stripHtml = (html) => {
    if (!html) return "";
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent || "";
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleRegisterCourse = () => {
    if (!course) return;
    setTimeout(() => {
      navigate("/cohort/register", {
        state: { 
          cohort_id: course.cohorts?.[0]?.id || null, 
          course_id: course.id || null,
          cohortName: course.title,
          coursePrice: course.price,
          cohortPrice: course.cohorts?.[0]?.dollar_price || null,
          isPrivateClass: true
        },
      });
    }, 250);
  };

  if (loading) {
    return (
      <div className="private-details-loading-container">
        <div className="loading-spinner"></div>
        <p>Loading course details...</p>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="private-details-error-container">
        <div className="error-icon">⚠️</div>
        <h3>Course Not Found</h3>
        <p>The course you are looking for might have been moved or is currently unavailable.</p>
        <Link to="/private-courses" className="back-btn">
          <FaArrowLeft className="me-2" /> Back to  Courses
        </Link>
      </div>
    );
  }

  return (
    <div className="private-details-page-wrapper">
      <div className="private-details-back-nav">
        <Link to="/private-courses" className="back-link">
          <FaArrowLeft className="me-2" /> Back to  Courses
        </Link>
      </div>

      {/* Hero Block */}
      <section className="private-details-hero">
        <div className="hero-grid-overlay" aria-hidden="true" />
        <div className="hero-content">
          <div className="hero-badge">
            <span className="badge-dot" />
            1-on-1 Exclusive Training Program
          </div>
          <h1>{course.title}</h1>
          <span className="category-pill">
            <BiCategory className="pill-icon" /> {course.category || "Technology"}
          </span>
        </div>
      </section>

      {/* Content Grid */}
      <main className="private-details-main-grid">
        <div className="private-details-left-pane">
          {/* Description */}
          <section className="details-card-section description-section">
            <h2>About the Program</h2>
            <p className="description-text">{stripHtml(course.description)}</p>
          </section>

          {/* Curriculum */}
          {course.curriculum && course.curriculum.length > 0 && (
            <section className="details-card-section curriculum-section">
              <h2>Course Syllabus & Modules</h2>
              <div className="curriculum-accordion">
                {course.curriculum.map((module, idx) => (
                  <div className="accordion-item" key={idx}>
                    <div
                      className="accordion-header"
                      onClick={() => toggleSection(module.title)}
                    >
                      <span className="module-title-wrapper">
                        <span className="module-number">{(idx + 1).toString().padStart(2, "0")}</span>
                        {module.title}
                      </span>
                      <IoChevronDown
                        className={`chevron-icon ${expandedSections[module.title] ? "rotated" : ""}`}
                      />
                    </div>
                    {expandedSections[module.title] && (
                      <div className="accordion-body animate-slide-down">
                        <p>{module.description}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Sidebar Info Section */}
        <aside className="private-details-right-pane">
          <div className="details-sticky-sidebar">
            <div className="sidebar-feature-box">
              <h3>Program Overview</h3>
              
              <ul className="overview-list">
                {course.location && (
                  <li>
                    <FaMapMarkerAlt className="overview-icon" color="#2563eb" />
                    <div>
                      <span className="label">Location</span>
                      <span className="value">{course.location}</span>
                    </div>
                  </li>
                )}
                <li>
                  <FaClock className="overview-icon" />
                  <div>
                    <span className="label">Class Duration</span>
                    <span className="value">{course.cohorts?.[0]?.duration || "Flexible / Self-Paced"}</span>
                  </div>
                </li>
                <li>
                  <FaGraduationCap className="overview-icon" />
                  <div>
                    <span className="label">Delivery Format</span>
                    <span className="value">Private 1-on-1 Lessons</span>
                  </div>
                </li>
                <li>
                  <BiCategory className="overview-icon" />
                  <div>
                    <span className="label">Access Type</span>
                    <span className="value">Dedicated Direct Mentor Reviews</span>
                  </div>
                </li>
              </ul>
            </div>

            {/* Prerequisites */}
            {course.prerequisite && course.prerequisite.length > 0 && (
              <div className="sidebar-feature-box prerequisites-box">
                <h3><FaAward className="me-2 text-primary" /> Prerequisites</h3>
                <ul className="prereq-list">
                  {course.prerequisite.map((item, idx) => (
                    <li key={idx}>
                      <FaCheck className="check-icon" />
                      <span>{item.title}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Pricing Card */}
            <div className="sidebar-feature-box pricing-card">
              <div className="pricing-header-row">
                <h3>Private Rate</h3>
                <div className="currency-pill-toggle">
                  <button
                    className={`curr-btn ${currency === "NGN" ? "active" : ""}`}
                    onClick={() => setCurrency("NGN")}
                  >
                    ₦ NGN
                  </button>
                  <button
                    className={`curr-btn ${currency === "USD" ? "active" : ""}`}
                    onClick={() => setCurrency("USD")}
                  >
                    $ USD
                  </button>
                </div>
              </div>

              <div className="price-tag-large">
                {currency === "USD" ? (
                  <>
                    <span className="symbol">$</span>
                    {course.price ? parseFloat(course.price).toLocaleString() : "N/A"}
                  </>
                ) : (
                  <>
                    <span className="symbol">₦</span>
                    {rateLoading ? (
                      <span className="loading-rate">Loading...</span>
                    ) : usdToNgn && course.price ? (
                      (parseFloat(course.price) * usdToNgn).toLocaleString()
                    ) : (
                      "N/A"
                    )}
                  </>
                )}
              </div>
              <p className="pricing-subtext">All-inclusive access to curriculum updates, code challenges, and projects review.</p>

              <button className="enroll-primary-btn" onClick={handleRegisterCourse}>
                Enroll in Private Class
              </button>
            </div>
          </div>
        </aside>
      </main>
    </div>
  );
};

export default PrivateCourseDetails;
